"use client";
import { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { ButtonSize, ButtonVariant, RegularButton } from "@/stories/button/button";
import useMobile from "@/app/hooks/use-mobile";
import Image from "next/image";
import { postRiskConsent } from "@/app/api/service/stocks";
import LoadingIcon from "@/app/assets/icons/loading";
import { setAnalyticsEvent } from "@/app/utils/SetAnalyticsEvent";
import { useCleverTapEvents } from "@/app/hooks/use-clevertap";
// import { initBranch, trackEvent } from "@/app/utils/branchio";

interface RiskConsentProps {
    riskContent: string;
    product: string;
    setriskConsent: (value: boolean) => void;
    setconsentAccepted: (value: boolean) => void;
    setisMobileWidgetOpen?: (value: boolean) => void;
    onClosePopup: () => void;
}

export function HighRiskConsent({
    riskContent,
    product,
    onClosePopup,
    setriskConsent,
    setisMobileWidgetOpen,
    setconsentAccepted,
}: RiskConsentProps) {
    const [isChecked, setisChecked] = useState<boolean>(true);
    const [btnDisabled, setbtnDisabled] = useState<boolean>(false);
    const { isMobile } = useMobile();
    const { cleverTapTrackEvent } = useCleverTapEvents();

    const postConsent = async () => {
        setbtnDisabled(true);
        const payload = {
            type: "highRiskStock",
            consent: true,
            product: product ? product : "",
        };

        const result = await postRiskConsent({ payload });

        if (result.success) {
            if (setisMobileWidgetOpen) {
                setriskConsent(false)
                setisMobileWidgetOpen(false);
            }
            setAnalyticsEvent("", `US_Transaction_Risk_Invest`);
            cleverTapTrackEvent(`US_Transaction_Risk_Invest`, {});
            // initBranch()
            //     .then(() => {
            //         trackEvent(`US_Transaction_Risk_Invest`);
            //     })
            //     .catch((error) => console.error("Error initializing Branch:", error));
            setconsentAccepted(result.success);
        }
    };
    return (
        <Dialog open={true} onOpenChange={onClosePopup}>
            <DialogContent className="scale-90 !rounded-3xl p-6 md:scale-100 md:p-9 [&>button:last-child]:hidden">
                <VisuallyHidden.Root>
                    <DialogTitle>High Risk Modal</DialogTitle>
                </VisuallyHidden.Root>
                <div className="h-auto w-full">
                    <div className="flex items-center gap-s7 pb-[24px] pt-[17px] md:w-full md:pb-[32px] md:pt-[24px]">
                        <Image
                            src={`https://assets.incredmoney.com/images/webp/incred-money.webp`}
                            alt="InCred Money"
                            height={31}
                            width={87}
                            className="h-[31px] w-[87px]"
                        />
                        <div className="ml-6 text-center text-xl font-semibold leading-[130%] tracking-[-0.24px] md:ml-0 md:flex-1 md:text-3xl">
                            Please Note
                        </div>
                    </div>
                    <hr />
                    <p className="py-s8 text-md font-semibold md:py-s11">Disclaimer</p>
                    <p
                        className="w-[90%] text-wrap break-words text-sm font-regular leading-[160%] tracking-[0.14px]"
                        dangerouslySetInnerHTML={{ __html: riskContent }}></p>
                    <div className="flex flex-col md:gap-3 justify-between md:mt-6 md:flex-row">
                        <div className="flex items-center py-4 align-middle md:py-0">
                            <input
                                type="checkbox"
                                id="risk-consent"
                                className="h-4 w-4 cursor-pointer rounded-lg accent-orange-500 md:h-6 md:w-6"
                                checked={isChecked}
                                onChange={() => setisChecked(!isChecked)}
                            />
                            <label
                                htmlFor="risk-consent"
                                className="text-nowrap cursor-pointer p-[10px] text-sm font-semibold md:text-md"
                            >
                                I understand
                            </label>
                        </div>
                        <div className="flex items-center justify-center gap-4">
                            <RegularButton
                                className="max-w-full text-lg"
                                label="Close"
                                noIcon={true}
                                variant={ButtonVariant.SecondaryDefault}
                                size={isMobile ? ButtonSize.Small : ButtonSize.Large}
                                showRightIcon={false}
                                onClick={onClosePopup}
                            />
                            <RegularButton
                                className="max-w-full text-lg"
                                label={btnDisabled ? "" : "Continue"}
                                noIcon={!btnDisabled ? true : false}
                                icon={
                                    btnDisabled ? <LoadingIcon className="animate-spin" /> : <></>
                                }
                                variant={ButtonVariant.PrimaryDefault}
                                size={isMobile ? ButtonSize.Small : ButtonSize.Large}
                                showRightIcon={false}
                                onClick={postConsent}
                                disabled={!isChecked || btnDisabled}
                            />
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
